import React, { useState } from 'react';
import { X, Car, Fuel, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface Reserva {
  id: string;
  veiculo: string;
  nome: string;
  motivo: string;
  empresa: string;
  data_retirada: string;
  horario_retirada: string;
  data_entrega: string;
  horario_entrega: string;
}

interface Props {
  reserva: Reserva;
  onClose: () => void;
  onSuccess: () => void;
}

const niveisComb = [
  { value: 'vazio', label: 'Vazio', bars: 0 },
  { value: '1/4', label: '1/4', bars: 1 },
  { value: '1/2', label: '1/2', bars: 2 },
  { value: '3/4', label: '3/4', bars: 3 },
  { value: 'cheio', label: 'Cheio', bars: 4 },
];

export const VeiculoChecklistModal: React.FC<Props> = ({ reserva, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    km_saida: '',
    nivel_combustivel: '1/2',
    pneus_ok: false,
    documentacao_ok: false,
    farois_ok: false,
    pisca_alerta_ok: false,
    luz_freio_ok: false,
    avarias: '',
    observacoes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.km_saida) {
      toast.error('Informe o KM do veículo na saída.');
      return;
    }

    setLoading(true);
    try {
      const checklistPayload = {
        km_saida: parseInt(form.km_saida),
        nivel_combustivel: form.nivel_combustivel,
        pneus_ok: form.pneus_ok,
        documentacao_ok: form.documentacao_ok,
        farois_ok: form.farois_ok,
        pisca_alerta_ok: form.pisca_alerta_ok,
        luz_freio_ok: form.luz_freio_ok,
        avarias: form.avarias || null,
        observacoes: form.observacoes || null,
      };

      const { error: checklistError } = await supabase
        .from('veiculo_checklist')
        .insert({
          reserva_id: reserva.id,
          usuario_id: user?.dbId,
          ...checklistPayload,
        });

      if (checklistError) throw checklistError;

      const { error: statusError } = await supabase
        .from('reserva_veiculos')
        .update({
          status: 'approved',
          checklist_data: checklistPayload,
        })
        .eq('id', reserva.id);

      if (statusError) throw statusError;

      toast.success('Checklist preenchido! Reserva confirmada.');
      onSuccess();
    } catch (err: any) {
      console.error('[CHECKLIST] Error:', err);
      toast.error('Erro ao salvar checklist: ' + (err.message || 'Tente novamente'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg my-6">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <Car className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Checklist de Saída</h2>
                <p className="text-blue-200 text-sm">{reserva.veiculo}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 mb-6">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
              Sua reserva foi aprovada! Preencha o checklist abaixo para confirmar a retirada do veículo.
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Retirada: {reserva.data_retirada} às {reserva.horario_retirada?.slice(0, 5)} — Entrega: {reserva.data_entrega} às {reserva.horario_entrega?.slice(0, 5)}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                KM atual do veículo *
              </label>
              <input
                type="number"
                value={form.km_saida}
                onChange={(e) => setForm({ ...form, km_saida: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 45823"
                min={0}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                <span className="flex items-center gap-2">
                  <Fuel className="w-4 h-4 text-amber-500" />
                  Nível de Combustível *
                </span>
              </label>
              <div className="grid grid-cols-5 gap-2">
                {niveisComb.map((n) => (
                  <button
                    key={n.value}
                    type="button"
                    onClick={() => setForm({ ...form, nivel_combustivel: n.value })}
                    className={`py-2 px-1 rounded-lg border-2 text-center transition-all ${
                      form.nivel_combustivel === n.value
                        ? 'border-amber-500 bg-amber-50 text-amber-700 font-semibold'
                        : 'border-gray-200 text-gray-600 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex justify-center gap-0.5 mb-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`w-1.5 h-3 rounded-sm ${
                            i < n.bars
                              ? 'bg-amber-500'
                              : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs">{n.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">Condições do Veículo</label>
              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.pneus_ok ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}
                  onClick={() => setForm({ ...form, pneus_ok: !form.pneus_ok })}
                >
                  {form.pneus_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.pneus_ok}
                  onChange={(e) => setForm({ ...form, pneus_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Pneus em boas condições</span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.documentacao_ok ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}
                  onClick={() => setForm({ ...form, documentacao_ok: !form.documentacao_ok })}
                >
                  {form.documentacao_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.documentacao_ok}
                  onChange={(e) => setForm({ ...form, documentacao_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  Documentação presente (CRLV, CNH, etc.)
                </span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.farois_ok ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}
                  onClick={() => setForm({ ...form, farois_ok: !form.farois_ok })}
                >
                  {form.farois_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.farois_ok}
                  onChange={(e) => setForm({ ...form, farois_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Faróis funcionando (baixo/alto)</span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.pisca_alerta_ok ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}
                  onClick={() => setForm({ ...form, pisca_alerta_ok: !form.pisca_alerta_ok })}
                >
                  {form.pisca_alerta_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.pisca_alerta_ok}
                  onChange={(e) => setForm({ ...form, pisca_alerta_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Pisca alerta (luzes de emergência) funcionando</span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.luz_freio_ok ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}
                  onClick={() => setForm({ ...form, luz_freio_ok: !form.luz_freio_ok })}
                >
                  {form.luz_freio_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.luz_freio_ok}
                  onChange={(e) => setForm({ ...form, luz_freio_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Luzes de freio funcionando</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  Avarias observadas
                </span>
              </label>
              <textarea
                value={form.avarias}
                onChange={(e) => setForm({ ...form, avarias: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={2}
                placeholder="Descreva qualquer avaria existente no veículo (arranhões, amassados, etc.)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Observações adicionais</label>
              <textarea
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={2}
                placeholder="Outras observações relevantes..."
              />
            </div>

            <div className="flex gap-3 pt-2 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? 'Salvando...' : 'Confirmar Retirada'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

